import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

/**
 * EmbeddedModule — shared scaffold for every new regulatory module.
 * Composes:
 *   - Branded header with title, tagline, and optional KPI strip
 *   - Optional `children` rendered above the iframe (native data section)
 *   - Iframe of an external Sanmiggrc/partner prototype with reload + open-in-tab
 */
const EmbeddedModule = ({
  eyebrow,
  title,
  tagline,
  kpis = [],
  embedUrl,
  embedTitle,
  accentClass = 'from-blue-600 to-indigo-600',
  badgeText,
  testId,
  children
}) => {
  const [reloadKey, setReloadKey] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => {
    setIframeLoading(true);
  }, [reloadKey, embedUrl]);

  return (
    <div
      className="flex-1 overflow-y-auto bg-gray-50 flex flex-col"
      data-testid={testId}
    >
      {/* Hero */}
      <div className={`bg-gradient-to-r ${accentClass} text-white px-8 py-7`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-widest text-white/70 mb-1">
              {eyebrow}
            </p>
            <h1 className="text-2xl md:text-3xl font-bold leading-tight">
              {title}
            </h1>
            {tagline && (
              <p className="text-sm md:text-base text-white/80 mt-1.5 max-w-3xl">
                {tagline}
              </p>
            )}
          </div>
          {badgeText && (
            <Badge className="bg-white/15 hover:bg-white/25 text-white border-white/20">
              {badgeText}
            </Badge>
          )}
        </div>

        {kpis.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {kpis.map((k, idx) => (
              <div
                key={k.label || `kpi-${idx}`}
                className="bg-white/10 backdrop-blur rounded-lg px-4 py-3 border border-white/15"
                data-testid={`kpi-${idx}`}
              >
                <p className="text-[11px] uppercase tracking-wider text-white/70">
                  {k.label}
                </p>
                <p className="text-xl font-bold mt-1">{k.value}</p>
                {k.hint && (
                  <p className="text-[11px] text-white/60 mt-0.5">{k.hint}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="px-6 md:px-8 py-6 space-y-6">
        {children}

        {embedUrl && (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-gray-500">
                  Live Workspace
                </p>
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {embedTitle || embedUrl}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReloadKey((k) => k + 1)}
                  data-testid="embed-reload"
                >
                  <RefreshCw className="w-4 h-4 mr-1.5" />
                  Reload
                </Button>
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="embed-open-tab"
                >
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-1.5" />
                    Open in tab
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative bg-white" style={{ height: '780px' }}>
              {iframeLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 pointer-events-none">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
                </div>
              )}
              <iframe
                ref={iframeRef}
                key={reloadKey}
                title={embedTitle || 'embedded'}
                src={embedUrl}
                onLoad={() => setIframeLoading(false)}
                className="w-full h-full border-0"
                sandbox="allow-forms allow-modals allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-downloads"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmbeddedModule;
